interface AnalyticsEvent {
  name: string;
  category: string;
  action: string;
  label?: string;
  value?: number;
  properties?: Record<string, unknown>;
  timestamp: number;
  userId?: string;
  sessionId: string;
  url: string;
  userAgent: string;
}

interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  url: string;
  sessionId: string;
}

interface ErrorEvent {
  message: string;
  stack?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  timestamp: number;
  url: string;
  userAgent: string;
  sessionId: string;
  userId?: string;
}

class Analytics {
  private sessionId: string;
  private userId?: string;
  private isEnabled: boolean;
  private debugMode: boolean;
  private events: AnalyticsEvent[] = [];
  private performanceMetrics: PerformanceMetric[] = [];
  private errors: ErrorEvent[] = [];

  constructor() {
    this.sessionId = this.generateSessionId();
    this.isEnabled = !window.location.hostname.includes('localhost');
    this.debugMode = process.env.NODE_ENV === 'development';
    this.init();
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private init() {
    // Set up error tracking
    window.addEventListener('error', this.handleError.bind(this));
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this));

    // Set up performance monitoring
    this.setupPerformanceMonitoring();

    // Set up page view tracking
    this.trackPageView();

    // Set up visibility change tracking
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

    // Set up beforeunload tracking
    window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
  }

  private handleError(event: ErrorEvent) {
    this.trackError({
      message: event.message,
      stack: (event as any).error?.stack,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      sessionId: this.sessionId,
      userId: this.userId,
    });
  }

  private handleUnhandledRejection(event: PromiseRejectionEvent) {
    this.trackError({
      message: `Unhandled Promise Rejection: ${event.reason}`,
      stack: event.reason?.stack,
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      sessionId: this.sessionId,
      userId: this.userId,
    });
  }

  private setupPerformanceMonitoring() {
    if ('PerformanceObserver' in window) {
      // Monitor Core Web Vitals
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'navigation') {
            const navEntry = entry as PerformanceNavigationTiming;
            this.trackPerformance('page_load_time', navEntry.loadEventEnd - navEntry.loadEventStart);
            this.trackPerformance('dom_content_loaded', navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart);
            this.trackPerformance('first_contentful_paint', navEntry.loadEventEnd - navEntry.fetchStart);
          }
        }
      });

      observer.observe({ type: 'navigation', buffered: true });
    }
  }

  private handleVisibilityChange() {
    if (document.hidden) {
      this.track('page_visibility', 'engagement', 'hidden');
    } else {
      this.track('page_visibility', 'engagement', 'visible');
    }
  }

  private handleBeforeUnload() {
    this.track('page_unload', 'engagement', 'beforeunload');
    this.flush();
  }

  public setUserId(userId: string) {
    this.userId = userId;
  }

  public enable() {
    this.isEnabled = true;
  }

  public disable() {
    this.isEnabled = false;
  }

  public track(name: string, category: string, action: string, label?: string, value?: number, properties?: Record<string, unknown>) {
    if (!this.isEnabled) return;

    const event: AnalyticsEvent = {
      name,
      category,
      action,
      label,
      value,
      properties,
      timestamp: Date.now(),
      userId: this.userId,
      sessionId: this.sessionId,
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    this.events.push(event);

    if (this.debugMode) {
      console.log('[Analytics] Event:', event);
    }

    // Send to server if you have an endpoint
    // this.sendToServer('/api/analytics/events', event);
  }

  public trackPageView(path?: string) {
    this.track('page_view', 'navigation', 'view', path || window.location.pathname);
  }

  public trackPerformance(name: string, value: number) {
    if (!this.isEnabled) return;

    const metric: PerformanceMetric = {
      name,
      value,
      timestamp: Date.now(),
      url: window.location.href,
      sessionId: this.sessionId,
    };

    this.performanceMetrics.push(metric);

    if (this.debugMode) {
      console.log('[Analytics] Performance:', metric);
    }

    // Send to server if you have an endpoint
    // this.sendToServer('/api/analytics/performance', metric);
  }

  public trackError(error: ErrorEvent) {
    this.errors.push(error);

    if (this.debugMode) {
      console.error('[Analytics] Error:', error);
    }

    // Send to server if you have an endpoint
    // this.sendToServer('/api/analytics/errors', error);
  }

  public trackUserAction(action: string, element?: string, properties?: Record<string, unknown>) {
    this.track('user_action', 'interaction', action, element, undefined, properties);
  }

  public trackButtonClick(buttonText: string, location?: string) {
    this.trackUserAction('click', `button_${buttonText}`, { location });
  }

  public trackFormSubmit(formName: string, success: boolean) {
    this.track('form_submit', 'conversion', success ? 'success' : 'error', formName);
  }

  public trackSearchQuery(query: string, resultsCount?: number) {
    this.track('search', 'engagement', 'query', query, resultsCount);
  }

  public trackDownload(filename: string, fileType: string) {
    this.track('download', 'engagement', 'file', filename, undefined, { fileType });
  }

  public trackOutboundLink(url: string, linkText?: string) {
    this.track('outbound_link', 'engagement', 'click', linkText, undefined, { url });
  }

  public trackScrollDepth(percentage: number) {
    this.track('scroll_depth', 'engagement', 'scroll', `${percentage}%`, percentage);
  }

  public trackTimeOnPage(duration: number) {
    this.track('time_on_page', 'engagement', 'duration', window.location.pathname, duration);
  }

  public flush() {
    // Send all pending events to server
    if (this.events.length > 0) {
      // this.sendToServer('/api/analytics/batch', { events: this.events });
      this.events = [];
    }

    if (this.performanceMetrics.length > 0) {
      // this.sendToServer('/api/analytics/performance/batch', { metrics: this.performanceMetrics });
      this.performanceMetrics = [];
    }

    if (this.errors.length > 0) {
      // this.sendToServer('/api/analytics/errors/batch', { errors: this.errors });
      this.errors = [];
    }
  }

  private async sendToServer(endpoint: string, data: unknown) {
    try {
      await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('Failed to send analytics data:', error);
    }
  }

  // Real User Monitoring methods
  public startRUM() {
    // Monitor Core Web Vitals
    if ('web-vitals' in window) {
      // Integration with web-vitals library if available
      this.setupWebVitals();
    }

    // Monitor resource loading
    this.monitorResourceLoading();

    // Monitor user interactions
    this.monitorUserInteractions();
  }

  private setupWebVitals() {
    // This would integrate with the web-vitals library
    // For now, we'll use Performance Observer
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
                 for (const entry of list.getEntries()) {
           if (entry.entryType === 'largest-contentful-paint') {
             this.trackPerformance('LCP', entry.startTime);
           }
           if (entry.entryType === 'first-input') {
             const fidEntry = entry as any;
             this.trackPerformance('FID', fidEntry.processingStart - entry.startTime);
           }
           if (entry.entryType === 'layout-shift') {
             const clsEntry = entry as any;
             this.trackPerformance('CLS', clsEntry.value);
           }
         }
      });

      observer.observe({ type: 'largest-contentful-paint', buffered: true });
      observer.observe({ type: 'first-input', buffered: true });
      observer.observe({ type: 'layout-shift', buffered: true });
    }
  }

  private monitorResourceLoading() {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'resource') {
            const resource = entry as PerformanceResourceTiming;
            this.trackPerformance(`resource_${resource.name}`, resource.duration);
          }
        }
      });

      observer.observe({ type: 'resource', buffered: true });
    }
  }

  private monitorUserInteractions() {
    // Monitor clicks
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'BUTTON' || target.tagName === 'A') {
        this.trackUserAction('click', target.textContent || target.tagName);
      }
    });

    // Monitor form submissions
    document.addEventListener('submit', (event) => {
      const form = event.target as HTMLFormElement;
      this.trackFormSubmit(form.name || 'unnamed_form', true);
    });
  }
}

// Create and export singleton instance
export const analytics = new Analytics();

// Export types for TypeScript usage
export type { AnalyticsEvent, PerformanceMetric, ErrorEvent }; 